/**
 * useGeoFeatureData - Hook for loading geographic feature data.
 *
 * Consolidates the loading of countries, lakes, provinces, and cities
 * into a single hook with proper loading/error states.
 */

import { useState, useEffect } from 'react'
import type { CountryData, LakeData, ProvinceData, CityData } from '../types/features'

/**
 * Result type for the geo feature data hook.
 */
export interface GeoFeatureData {
  /** Country boundary and metadata */
  countries: CountryData[]
  /** Lake polygons */
  lakes: LakeData[]
  /** Province/state boundaries */
  provinces: ProvinceData[]
  /** City points with population data */
  cities: CityData[]
  /** True while any data is still loading */
  loading: boolean
  /** Error message if any fetch failed, null otherwise */
  error: string | null
}

/**
 * Fetches a JSON file and returns the parsed data.
 * Throws an error with a descriptive message on failure.
 */
async function fetchJson<T>(url: string, name: string): Promise<T> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to load ${name}: ${response.status} ${response.statusText}`)
  }
  return response.json()
}

/**
 * Hook for loading all geographic feature data needed by the globe.
 *
 * Loads countries, lakes, provinces, and cities in parallel using Promise.all.
 * Provides loading and error states for proper UI feedback.
 *
 * @example
 * ```tsx
 * function GlobeScene() {
 *   const { countries, lakes, provinces, cities, loading, error } = useGeoFeatureData()
 *
 *   if (loading) return <LoadingSpinner />
 *   if (error) return <ErrorMessage message={error} />
 *
 *   return <CountryGlobe countries={countries} lakes={lakes} ... />
 * }
 * ```
 */
export function useGeoFeatureData(): GeoFeatureData {
  const [countries, setCountries] = useState<CountryData[]>([])
  const [lakes, setLakes] = useState<LakeData[]>([])
  const [provinces, setProvinces] = useState<ProvinceData[]>([])
  const [cities, setCities] = useState<CityData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function loadData() {
      try {
        const [countriesData, lakesData, provincesData, citiesData] = await Promise.all([
          fetchJson<CountryData[]>('/countries.json', 'countries'),
          fetchJson<LakeData[]>('/lakes.json', 'lakes'),
          fetchJson<ProvinceData[]>('/provinces.json', 'provinces'),
          fetchJson<CityData[]>('/cities.json', 'cities'),
        ])

        if (cancelled) return

        setCountries(countriesData)
        setLakes(lakesData)
        setProvinces(provincesData)
        setCities(citiesData)
        setLoading(false)
      } catch (err) {
        if (cancelled) return

        console.error('Failed to load geo feature data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load geographic data')
        setLoading(false)
      }
    }

    loadData()

    return () => {
      cancelled = true
    }
  }, [])

  return {
    countries,
    lakes,
    provinces,
    cities,
    loading,
    error,
  }
}
